import { PageHeader, DataTable, LoadingState, EmptyState, FilterBar } from '../components/AdminPageUI';
import SummaryStrip, { SummaryMetric } from '../components/SummaryStrip';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Box, Typography, Card, CardContent, Container, Grid, MenuItem, TextField, Button, Table, TableBody, TableCell, TableHead, TableRow, Paper, CircularProgress, Stack, Avatar, Chip, LinearProgress, Alert, Divider, useMediaQuery } from '@mui/material';
import {
  Print as PrintIcon,
  Assessment as AssessmentIcon,
  PictureAsPdf as PdfIcon,
  Groups as GroupsIcon,
  TrendingUp as TrendingUpIcon,
  WarningAmber as WarningIcon,
  FactCheck as FactCheckIcon,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import api from '../utils/Api';

const ATTENDANCE_DAYS = ['M', 'T', 'W', 'TH', 'F'];
const CHART_COLORS = ['#2563eb', '#16a34a', '#f59e0b', '#dc2626', '#7c3aed', '#0891b2', '#db2777'];

const filterFieldSx = {
  '& .MuiInputBase-root': {
    minHeight: 56,
    borderRadius: 2,
    alignItems: 'center',
  },
  '& .MuiInputBase-input': {
    fontSize: 15,
    fontWeight: 600,
  },
  '& .MuiSelect-select': {
    minHeight: '24px !important',
    display: 'flex',
    alignItems: 'center',
    py: '15px',
  },
  '& .MuiInputLabel-root': {
    fontSize: 15,
    fontWeight: 600,
  },
};

const toNumber = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const toWholeNumber = (value) => Math.round(toNumber(value));

const formatPercent = (value) => `${toWholeNumber(value)}%`;

const truncateLabel = (value, length = 16) => {
  const text = String(value || '');
  return text.length > length ? `${text.slice(0, length - 1)}…` : text;
};

const getTermLabel = (term) => {
  const name = term?.name || term?.term || 'Term';
  return term?.academicYear ? `${name} • ${term.academicYear}` : name;
};

const getTermSortValue = (term) => {
  const academicYear = String(term?.academicYear || '');
  const yearMatch = academicYear.match(/\d{4}/);
  const startYear = yearMatch ? Number(yearMatch[0]) : 0;
  const termMatch = String(term?.term || term?.name || '').match(/\d+/);
  const termNumber = termMatch ? Number(termMatch[0]) : 0;
  return startYear * 10 + termNumber;
};

const getRecordId = (record) => record?._id || record?.id || '';

const getStoredUser = () => {
  try {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  } catch (error) {
    return null;
  }
};

const getStoredSchoolId = () => {
  const user = getStoredUser();
  return user?.school?._id || user?.school || '';
};

const isTermCurrentByDate = (term, referenceDate = new Date()) => {
  if (!term?.startDate || !term?.endDate) return false;

  const startDate = new Date(term.startDate);
  const endDate = new Date(term.endDate);

  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return false;

  startDate.setHours(0, 0, 0, 0);
  endDate.setHours(23, 59, 59, 999);

  return referenceDate >= startDate && referenceDate <= endDate;
};

const getDefaultTerm = (terms = []) => {
  if (!Array.isArray(terms) || !terms.length) return null;

  const flaggedCurrentTerm = terms.find((term) => {
    const status = String(term?.status || '').toLowerCase();
    return term?.isCurrent === true || term?.current === true || status === 'current' || status === 'active';
  });

  if (flaggedCurrentTerm) return flaggedCurrentTerm;

  const dateCurrentTerm = terms.find((term) => isTermCurrentByDate(term));
  if (dateCurrentTerm) return dateCurrentTerm;

  return [...terms]
    .filter((term) => getRecordId(term))
    .sort((a, b) => getTermSortValue(b) - getTermSortValue(a))[0] || terms[0];
};

const getComparableTerms = (terms, selectedTermId, limit = 4) => {
  if (!selectedTermId) return [];
  const sortedTerms = [...terms]
    .filter((term) => term?._id)
    .sort((a, b) => getTermSortValue(a) - getTermSortValue(b));
  const selectedIndex = sortedTerms.findIndex((term) => String(term._id) === String(selectedTermId));
  if (selectedIndex < 0) return [];
  return sortedTerms.slice(Math.max(0, selectedIndex - limit + 1), selectedIndex + 1);
};

const extractReportCards = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.reportCards)) return data.reportCards;
  if (Array.isArray(data?.reports)) return data.reports;
  return [];
};

const extractAttendanceRecords = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.attendance)) return data.attendance;
  if (Array.isArray(data?.records)) return data.records;
  return [];
};

const getSubjectLabel = (subjectRow) => {
  const subject = subjectRow?.subject || {};
  return (
    subject.name ||
    subject.subjectName ||
    subjectRow?.subjectName ||
    subjectRow?.name ||
    'Unknown Subject'
  );
};

const getSubjectKey = (subjectRow) => {
  const subject = subjectRow?.subject || {};
  const id = subject?._id || subject?.id || subjectRow?.subjectId || subjectRow?.id;
  if (id) return String(id);
  return getSubjectLabel(subjectRow).trim().toLowerCase().replace(/\s+/g, '-');
};

const getShortGrade = (grade) => {
  const text = String(grade || '').trim();
  const match = text.match(/\(([^)]+)\)/);
  return match?.[1] || text || '-';
};

const getAttendanceTotalPresent = (record) => {
  const attendance = Array.isArray(record?.attendance) ? record.attendance : [];
  return attendance.reduce((sum, week) => {
    const totalPresent = Number(week?.totalPresent);
    if (Number.isFinite(totalPresent)) return sum + totalPresent;
    return sum + ATTENDANCE_DAYS.filter((day) => week?.days?.[day] === 'present').length;
  }, 0);
};

const calculateTimesOpenFromAttendance = (records) => {
  const openDays = new Set();

  records.forEach((record) => {
    const attendance = Array.isArray(record?.attendance) ? record.attendance : [];
    attendance.forEach((week) => {
      const weekNumber = Number(week?.weekNumber) || Number(week?.week) || 0;
      if (!weekNumber || !week?.days) return;
      ATTENDANCE_DAYS.forEach((day) => {
        const status = week.days[day];
        if (status && status !== 'notmarked') openDays.add(`${weekNumber}-${day}`);
      });
    });
  });

  return openDays.size;
};

const calculateAttendanceSummary = (records) => {
  if (!records.length) {
    return {
      classAveragePresent: 0,
      timesOpen: 0,
      classSize: 0,
      classAverageRate: 0,
    };
  }

  const totals = records.map(getAttendanceTotalPresent);
  const classSize = totals.length;
  const classAveragePresent = classSize
    ? Math.round(totals.reduce((sum, value) => sum + value, 0) / classSize)
    : 0;
  const timesOpen = calculateTimesOpenFromAttendance(records);

  return {
    classAveragePresent,
    timesOpen,
    classSize,
    classAverageRate: timesOpen ? Math.round((classAveragePresent / timesOpen) * 100) : 0,
  };
};

const getReportAverage = (report) => {
  const explicitAverage = Number(report?.averageMarks);
  if (Number.isFinite(explicitAverage) && explicitAverage > 0) return explicitAverage;
  const subjects = Array.isArray(report?.subjects) ? report.subjects : [];
  if (!subjects.length) return 0;
  return subjects.reduce((sum, subject) => sum + toNumber(subject.total), 0) / subjects.length;
};

const buildSubjectAverages = (reports) => {
  const subjectTotals = {};

  reports.forEach((report) => {
    const subjects = Array.isArray(report?.subjects) ? report.subjects : [];
    subjects.forEach((subject) => {
      const key = getSubjectKey(subject);
      if (!key) return;
      const total = toNumber(subject.total);
      if (!Number.isFinite(total)) return;

      if (!subjectTotals[key]) {
        subjectTotals[key] = {
          key,
          label: getSubjectLabel(subject),
          sum: 0,
          count: 0,
          urgentCount: 0,
          supportCount: 0,
          sbaSum: 0,
          examsSum: 0,
        };
      }

      subjectTotals[key].sum += total;
      subjectTotals[key].count += 1;
      subjectTotals[key].sbaSum += toNumber(subject.scaledSba);
      subjectTotals[key].examsSum += toNumber(subject.scaledExams);
      if (total < 50) subjectTotals[key].urgentCount += 1;
      if (total < 65) subjectTotals[key].supportCount += 1;
    });
  });

  return Object.values(subjectTotals)
    .map((item) => ({
      ...item,
      average: item.count ? Math.round(item.sum / item.count) : 0,
      sbaAverage: item.count ? Math.round(item.sbaSum / item.count) : 0,
      examsAverage: item.count ? Math.round(item.examsSum / item.count) : 0,
      supportRate: item.count ? Math.round((item.supportCount / item.count) * 100) : 0,
      shortLabel: truncateLabel(item.label, 18),
    }))
    .sort((a, b) => b.average - a.average || a.label.localeCompare(b.label));
};

const buildGradeDistribution = (reports) => {
  const distribution = {};

  reports.forEach((report) => {
    const subjects = Array.isArray(report?.subjects) ? report.subjects : [];
    subjects.forEach((subject) => {
      const grade = getShortGrade(subject.grade);
      distribution[grade] = (distribution[grade] || 0) + 1;
    });
  });

  return Object.entries(distribution)
    .map(([grade, count], index) => ({
      grade,
      count,
      color: CHART_COLORS[index % CHART_COLORS.length],
    }))
    .sort((a, b) => b.count - a.count || a.grade.localeCompare(b.grade));
};

const buildClassAnalysis = (reportCards, attendanceRecords, history = []) => {
  const reportsWithMarks = reportCards.filter((report) => Array.isArray(report?.subjects) && report.subjects.length > 0);
  const sortedReports = [...reportsWithMarks].sort((a, b) => getReportAverage(b) - getReportAverage(a));
  const classAverage = reportsWithMarks.length
    ? Math.round(sortedReports.reduce((sum, report) => sum + getReportAverage(report), 0) / reportsWithMarks.length)
    : 0;
  const subjectAverages = buildSubjectAverages(reportsWithMarks);
  const subjectAveragesWeakestFirst = [...subjectAverages].sort((a, b) => a.average - b.average || b.supportCount - a.supportCount);
  const gradeDistribution = buildGradeDistribution(reportsWithMarks);
  const attendanceSummary = calculateAttendanceSummary(attendanceRecords);
  const classTrend = history
    .map((item) => {
      const itemReports = Array.isArray(item.reports) ? item.reports.filter((report) => Array.isArray(report?.subjects) && report.subjects.length > 0) : [];
      const average = itemReports.length
        ? Math.round(itemReports.reduce((sum, report) => sum + getReportAverage(report), 0) / itemReports.length)
        : 0;
      const itemAttendance = item.attendanceSummary || calculateAttendanceSummary(item.attendanceRecords || []);
      return {
        label: item.label,
        average,
        attendance: itemAttendance.classAverageRate || 0,
      };
    })
    .filter((item) => item.average > 0 || item.attendance > 0);

  const weakSubjectAlerts = subjectAveragesWeakestFirst
    .filter((subject) => subject.average < 65 || subject.urgentCount > 0 || subject.supportCount > 0)
    .slice(0, 6)
    .map((subject) => {
      if (subject.average < 50 || subject.urgentCount > 0) {
        return {
          tone: 'error',
          title: `${subject.label}: urgent attention`,
          description: `${subject.urgentCount} student${subject.urgentCount === 1 ? '' : 's'} below 50. Class average is ${subject.average}%.`,
        };
      }
      return {
        tone: 'warning',
        title: `${subject.label}: needs support`,
        description: `${subject.supportCount} student${subject.supportCount === 1 ? '' : 's'} below 65. Class average is ${subject.average}%.`,
      };
    });

  return {
    totalStudents: reportCards.length,
    studentsWithMarks: reportsWithMarks.length,
    classAverage,
    aboveAverageCount: reportsWithMarks.filter((report) => getReportAverage(report) >= classAverage).length,
    supportStudentCount: reportsWithMarks.filter((report) => getReportAverage(report) < 65).length,
    urgentStudentCount: reportsWithMarks.filter((report) => getReportAverage(report) < 50).length,
    topStudent: sortedReports[0] || null,
    lowestStudent: sortedReports[sortedReports.length - 1] || null,
    subjectAverages,
    subjectAveragesWeakestFirst,
    strongestSubject: subjectAverages[0] || null,
    weakestSubject: subjectAveragesWeakestFirst[0] || null,
    gradeDistribution,
    attendanceSummary,
    classTrend,
    weakSubjectAlerts,
    studentRankings: sortedReports.map((report, index) => ({
      name: report.studentInfo?.name || 'Unknown Student',
      average: Math.round(getReportAverage(report)),
      position: report.position || index + 1,
      subjectCount: Array.isArray(report.subjects) ? report.subjects.length : 0,
    })),
  };
};

const AnalysisStatCard = ({ label, value, helper, color = 'primary.main' }) => (
  <SummaryMetric
    sx={{
      height: '100%',
      width: '100%',
      border: '1px solid',
      borderColor: color || 'divider',
      boxShadow: 3,
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: 6,
      },
    }}
  >
    <CardContent sx={{ p: 2.25 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.75, color }}>
        {value}
      </Typography>
      {helper ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
          {helper}
        </Typography>
      ) : null}
    </CardContent>
  </SummaryMetric>
);

const PageStatCard = ({ icon, label, value, helper, color, isMobile }) => (
  <SummaryMetric
    sx={{
      height: '100%',
      width: '100%',
      border: '1px solid',
      borderColor: color || 'divider',
      boxShadow: isMobile ? 1 : 3,
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: isMobile ? 'none' : 'translateY(-4px)',
        boxShadow: isMobile ? 2 : 6,
      },
    }}
  >
    <CardContent sx={{ p: isMobile ? 2 : 3 }}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Avatar
          sx={{
            bgcolor: alpha(color, 0.12),
            color,
            width: isMobile ? 42 : 52,
            height: isMobile ? 42 : 52,
          }}
        >
          {icon}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase' }}
          >
            {label}
          </Typography>
          <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 900, color, lineHeight: 1.15 }}>
            {value}
          </Typography>
          {helper ? (
            <Typography variant="body2" color="text.secondary" noWrap={!isMobile}>
              {helper}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </CardContent>
  </SummaryMetric>
);

const ChartCard = ({ title, helper, children }) => (
  <Card
    sx={{
      height: '100%',
      width: '100%',
      border: '1px solid',
      borderColor: 'divider',
      boxShadow: 3,
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: 6,
      },
    }}
  >
    <CardContent sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5 }}>
        {title}
      </Typography>
      {helper ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {helper}
        </Typography>
      ) : null}
      {children}
    </CardContent>
  </Card>
);

const ScoreBar = ({ label, value, color = 'primary.main', helper }) => (
  <Box sx={{ mb: 1.5 }}>
    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 800 }}>
        {formatPercent(value)}
      </Typography>
    </Stack>
    <LinearProgress
      variant="determinate"
      value={Math.min(100, Math.max(0, toNumber(value)))}
      sx={{
        height: 8,
        borderRadius: 99,
        bgcolor: 'action.hover',
        '& .MuiLinearProgress-bar': {
          borderRadius: 99,
          bgcolor: color,
        },
      }}
    />
    {helper ? (
      <Typography variant="caption" color="text.secondary">
        {helper}
      </Typography>
    ) : null}
  </Box>
);

function ClassPerformanceAnalysisPanel({
  loading,
  error,
  selectedClassName,
  selectedTermName,
  analysis,
}) {
  if (!selectedClassName || !selectedTermName) {
    return (
      <Card sx={{ mb: 4, border: '1px solid', borderColor: 'divider', boxShadow: 3 }} className="no-print">
        <CardContent sx={{ py: 5, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Class Performance Analysis
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            Select an academic term and class to view analysis for any class.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card sx={{ mb: 4, border: '1px solid', borderColor: 'divider', boxShadow: 3 }} className="no-print">
        <CardContent sx={{ py: 5, textAlign: 'center' }}>
          <CircularProgress />
          <Typography color="text.secondary" sx={{ mt: 2 }}>
            Loading class performance analysis...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert severity="warning" sx={{ mb: 4 }} className="no-print">
        {error}
      </Alert>
    );
  }

  if (!analysis || analysis.studentsWithMarks === 0) {
    return (
      <Card sx={{ mb: 4, border: '1px solid', borderColor: 'divider', boxShadow: 3 }} className="no-print">
        <CardContent sx={{ py: 5, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            No performance analysis yet
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            Marks have not been found for {selectedClassName} in {selectedTermName}.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const subjectChartData = analysis.subjectAveragesWeakestFirst.slice(0, 10);
  const sbaExamChartData = analysis.subjectAveragesWeakestFirst.slice(0, 8);
  const studentChartData = analysis.studentRankings.slice(0, 6);

  return (
    <Box className="no-print" sx={{ mb: 4 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2.5 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 900 }}>
            Class Performance Analysis
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedClassName} • {selectedTermName} • all report-card subjects
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip color="primary" label={`${analysis.studentsWithMarks}/${analysis.totalStudents} with marks`} />
          <Chip color={analysis.urgentStudentCount ? 'error' : 'success'} label={`${analysis.urgentStudentCount} urgent`} />
          <Chip color={analysis.supportStudentCount ? 'warning' : 'success'} label={`${analysis.supportStudentCount} below 65`} />
        </Stack>
      </Stack>

      <SummaryStrip container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <AnalysisStatCard
            label="Class Average"
            value={formatPercent(analysis.classAverage)}
            helper={`${analysis.aboveAverageCount} students at or above class average`}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <AnalysisStatCard
            label="Top Student"
            value={analysis.topStudent?.studentInfo?.name || '-'}
            helper={analysis.topStudent ? `${formatPercent(getReportAverage(analysis.topStudent))} average` : ''}
            color="success.main"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <AnalysisStatCard
            label="Strongest Subject"
            value={analysis.strongestSubject?.label || '-'}
            helper={analysis.strongestSubject ? `${formatPercent(analysis.strongestSubject.average)} class average` : ''}
            color="primary.main"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <AnalysisStatCard
            label="Weakest Subject"
            value={analysis.weakestSubject?.label || '-'}
            helper={analysis.weakestSubject ? `${formatPercent(analysis.weakestSubject.average)} class average` : ''}
            color={analysis.weakestSubject?.average < 65 ? 'warning.main' : 'success.main'}
          />
        </Grid>
      </SummaryStrip>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
          gap: 2.5,
          alignItems: 'stretch',
        }}
      >
        <Box sx={{ display: 'flex', minWidth: 0 }}>
          <ChartCard
            title="Subject Average Chart"
            helper="Subjects are ordered from weakest to strongest so the class-wide problem areas are easy to see."
          >
            <Box sx={{ height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={subjectChartData} margin={{ top: 8, right: 16, bottom: 28, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="shortLabel" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={60} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(value) => [`${value}%`, 'Average']} />
                  <Bar dataKey="average" radius={[6, 6, 0, 0]}>
                    {subjectChartData.map((entry, index) => (
                      <Cell
                        key={`subject-bar-${entry.key}`}
                        fill={entry.average < 50 ? '#dc2626' : entry.average < 65 ? '#f59e0b' : CHART_COLORS[index % CHART_COLORS.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </ChartCard>
        </Box>

        <Box sx={{ display: 'flex', minWidth: 0 }}>
          <ChartCard
            title="Term-to-Term Progress"
            helper="Shows whether the selected class is improving or dropping across available terms."
          >
            {analysis.classTrend.length > 1 ? (
              <Box sx={{ height: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={analysis.classTrend} margin={{ top: 8, right: 16, bottom: 16, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(value, name) => [`${value}%`, name === 'average' ? 'Class average' : 'Attendance']} />
                    <Line type="monotone" dataKey="average" stroke="#2563eb" strokeWidth={3} dot={{ r: 4 }} />
                    <Line type="monotone" dataKey="attendance" stroke="#16a34a" strokeWidth={3} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Box sx={{ height: 320, display: 'grid', placeItems: 'center', textAlign: 'center', px: 3 }}>
                <Typography color="text.secondary">
                  Previous term data is not available yet. This chart will appear when the class has report cards for more than one term.
                </Typography>
              </Box>
            )}
          </ChartCard>
        </Box>

        <Box sx={{ display: 'flex', minWidth: 0 }}>
          <ChartCard
            title="SBA vs Exams"
            helper="Compares the class average SBA contribution and exams contribution by subject."
          >
            <Box sx={{ height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={sbaExamChartData} margin={{ top: 8, right: 16, bottom: 28, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="shortLabel" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={60} />
                  <YAxis domain={[0, 50]} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(value, name) => [`${value}/50`, name === 'sbaAverage' ? 'SBA' : 'Exams']} />
                  <Bar dataKey="sbaAverage" stackId="marks" fill="#2563eb" radius={[0, 0, 4, 4]} />
                  <Bar dataKey="examsAverage" stackId="marks" fill="#16a34a" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </ChartCard>
        </Box>

        <Box sx={{ display: 'flex', minWidth: 0 }}>
          <ChartCard
            title="Grade Distribution"
            helper="Groups all subject grades across the selected class."
          >
            {analysis.gradeDistribution.length > 0 ? (
              <Box sx={{ height: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={analysis.gradeDistribution}
                      dataKey="count"
                      nameKey="grade"
                      innerRadius={64}
                      outerRadius={105}
                      paddingAngle={3}
                      label={({ grade, count }) => `${grade}: ${count}`}
                    >
                      {analysis.gradeDistribution.map((entry) => (
                        <Cell key={`grade-${entry.grade}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [value, `Grade ${name}`]} />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Box sx={{ height: 320, display: 'grid', placeItems: 'center' }}>
                <Typography color="text.secondary">No grade data yet.</Typography>
              </Box>
            )}
          </ChartCard>
        </Box>

        <Box sx={{ display: 'flex', minWidth: 0 }}>
          <ChartCard
            title="Top Student Comparison"
            helper="The first six students are ranked by overall average for the selected class."
          >
            {studentChartData.length > 0 ? (
              <Box
                sx={{
                  mt: 1,
                  flex: 1,
                  minHeight: { xs: 360, md: 0 },
                  display: 'grid',
                  gridTemplateRows: `repeat(${studentChartData.length}, minmax(0, 1fr))`,
                  gap: { xs: 1.5, md: 2 },
                  alignContent: 'stretch',
                }}
              >
                {studentChartData.map((student, index) => {
                  const value = Math.min(100, Math.max(0, toNumber(student.average)));
                  const color = index === 0 ? '#2563eb' : index === 1 ? '#16a34a' : index === 2 ? '#f59e0b' : '#64748b';

                  return (
                    <Box
                      key={`${student.name}-${index}`}
                      sx={{
                        px: 1.5,
                        py: 1.25,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        minHeight: 0,
                        bgcolor: index === 0 ? alpha('#2563eb', 0.06) : 'background.default',
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: '100%', minWidth: 0 }}>
                        <Box
                          sx={{
                            width: 34,
                            height: 34,
                            borderRadius: '50%',
                            display: 'grid',
                            placeItems: 'center',
                            flexShrink: 0,
                            bgcolor: alpha(color, 0.12),
                            color,
                            fontSize: 13,
                            fontWeight: 900,
                          }}
                        >
                          {index + 1}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5} sx={{ mb: 1 }}>
                            <Typography variant="body2" noWrap sx={{ fontWeight: 800, fontSize: 14 }}>
                              {student.name}
                            </Typography>
                            <Typography variant="body2" sx={{ flexShrink: 0, color, fontWeight: 900, fontSize: 14 }}>
                              {formatPercent(value)}
                            </Typography>
                          </Stack>
                          <LinearProgress
                            variant="determinate"
                            value={value}
                            sx={{
                              height: 10,
                              borderRadius: 99,
                              bgcolor: alpha(color, 0.12),
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 99,
                                bgcolor: color,
                              },
                            }}
                          />
                        </Box>
                      </Stack>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Alert severity="info">No student ranking data is available yet.</Alert>
            )}
          </ChartCard>
        </Box>

        <Box sx={{ display: 'flex', minWidth: 0 }}>
          <ChartCard
            title="Weak Subject Alerts"
            helper="Highlights subjects below 50, below 65, or with many students needing support."
          >
            {analysis.weakSubjectAlerts.length > 0 ? (
              <Stack spacing={1.5}>
                {analysis.weakSubjectAlerts.map((alert) => (
                  <Alert key={alert.title} severity={alert.tone}>
                    <Typography sx={{ fontWeight: 800 }}>{alert.title}</Typography>
                    <Typography variant="body2">{alert.description}</Typography>
                  </Alert>
                ))}
              </Stack>
            ) : (
              <Alert severity="success">No class-wide weak subject alert for the selected class.</Alert>
            )}

            <Divider sx={{ my: 2 }} />
            <ScoreBar
              label="Class average attendance"
              value={analysis.attendanceSummary.classAverageRate}
              color="success.main"
              helper={
                analysis.attendanceSummary.timesOpen
                  ? `${analysis.attendanceSummary.classAveragePresent}/${analysis.attendanceSummary.timesOpen} days average, ${analysis.attendanceSummary.classSize} attendance records`
                  : 'Attendance data is not available for this term.'
              }
            />
          </ChartCard>
        </Box>

        <Box sx={{ display: 'flex', minWidth: 0 }}>
          <ChartCard
            title="Subject Class Detail"
            helper="Use this table to decide where teachers should revise, reteach, or give extra class practice."
          >
            <DataTable component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: 'background.default' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800 }}>Subject</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 800 }}>Average</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 800 }}>Below 50</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 800 }}>Below 65</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 800 }}>Support Rate</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analysis.subjectAveragesWeakestFirst.map((subject) => (
                    <TableRow key={subject.key} hover>
                      <TableCell sx={{ fontWeight: 700 }}>{subject.label}</TableCell>
                      <TableCell align="center">{formatPercent(subject.average)}</TableCell>
                      <TableCell align="center" sx={{ color: subject.urgentCount ? 'error.main' : 'text.secondary', fontWeight: 800 }}>
                        {subject.urgentCount}
                      </TableCell>
                      <TableCell align="center" sx={{ color: subject.supportCount ? 'warning.main' : 'text.secondary', fontWeight: 800 }}>
                        {subject.supportCount}
                      </TableCell>
                      <TableCell align="center">{formatPercent(subject.supportRate)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </DataTable>
          </ChartCard>
        </Box>
      </Box>
    </Box>
  );
}

export default function SbaV2AdminPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [loadingMarks, setLoadingMarks] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  
  const [terms, setTerms] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);

  const [selectedTerm, setSelectedTerm] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');

  const [records, setRecords] = useState([]);
  const [reportCards, setReportCards] = useState([]);
  const [analysisAttendanceRecords, setAnalysisAttendanceRecords] = useState([]);
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [error, setError] = useState(null);
  const [analysisError, setAnalysisError] = useState(null);
  const [schoolInfoDetails, setSchoolInfoDetails] = useState(null);
  const analysisRequestRef = useRef(0);
  const analysisCacheRef = useRef(new Map());

  // Fetch standard data on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoadingInitial(true);
        const schoolId = getStoredSchoolId();

        const [termsResult, classesResult] = await Promise.allSettled([
          api.get('/term/all', { params: { school: schoolId } }),
          api.get('/classes', { params: { school: schoolId } }),
        ]);

        if (termsResult.status === 'rejected') console.error('Terms fetch error:', termsResult.reason);
        if (classesResult.status === 'rejected') console.error('Classes fetch error:', classesResult.reason);

        const termsArray = termsResult.status === 'fulfilled'
          ? termsResult.value.data.allTerms || termsResult.value.data.terms || termsResult.value.data.data || termsResult.value.data || []
          : [];
        const classesArray = classesResult.status === 'fulfilled'
          ? classesResult.value.data.classes || classesResult.value.data.data || classesResult.value.data || []
          : [];

        setTerms(termsArray);
        setClasses(classesArray);
        setSelectedTerm((previousTerm) => {
          if (previousTerm) return previousTerm;
          const defaultTerm = getDefaultTerm(termsArray);
          return getRecordId(defaultTerm);
        });
        setSelectedClass((previousClass) => {
          if (previousClass) return previousClass;
          const defaultClass = classesArray.find((classItem) => getRecordId(classItem)) || null;
          return getRecordId(defaultClass);
        });
        
        if (!termsArray.length && !classesArray.length) {
           setError('Failed to load terms and classes.');
        }
      } catch (err) {
        console.error('Failed to load initial data', err);
        setError('Failed to load terms, classes, or subjects.');
      } finally {
        setLoadingInitial(false);
      }
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchSecondaryData = async () => {
      const fetchSubjects = async () => {
        try {
          const subjectsRes = await api.get('/subjects');
          return subjectsRes.data.subjects || subjectsRes.data.data || subjectsRes.data || [];
        } catch (error) {
          console.error('Subjects fetch error:', error);
          try {
            const fallbackRes = await api.get('/subjects/global/list');
            return fallbackRes.data.subjects || fallbackRes.data.data || fallbackRes.data || [];
          } catch (fallbackError) {
            console.error('Fallback subjects fetch error:', fallbackError);
            return [];
          }
        }
      };

      const [subjectsResult, schoolInfoResult] = await Promise.allSettled([
        fetchSubjects(),
        api.get('/school-info'),
      ]);

      if (cancelled) return;

      if (subjectsResult.status === 'fulfilled') {
        setSubjects(subjectsResult.value);
      }

      if (schoolInfoResult.status === 'fulfilled') {
        setSchoolInfoDetails(schoolInfoResult.value.data);
      } else {
        console.error('School Info fetch error:', schoolInfoResult.reason);
      }
    };

    fetchSecondaryData();

    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch marks when dropdowns change
  useEffect(() => {
    if (!selectedTerm || !selectedClass || !selectedSubject) {
      setRecords([]);
      return;
    }

    const fetchMarks = async () => {
      try {
        setLoadingMarks(true);
        setError(null);
        
        const schoolId = getStoredSchoolId();

        const res = await api.get(`/sba-v2/marks`, {
          params: {
            schoolId,
            classId: selectedClass,
            termId: selectedTerm,
            subjectId: selectedSubject
          }
        });

        if (res.data && res.data.success) {
          // Normalize if needed, or just use what backend provides
          const data = res.data.data;
          
          if (data && data.records && Array.isArray(data.records)) {
             setRecords(data.records);
          } else if (Array.isArray(data)) {
             setRecords(data);
          } else {
             setRecords([]);
          }
        } else {
          setRecords([]);
        }
      } catch (err) {
        console.error('Failed to fetch marks', err);
        setError(err.response?.data?.message || 'Failed to fetch SBA marks.');
        setRecords([]);
      } finally {
        setLoadingMarks(false);
      }
    };

    fetchMarks();
  }, [selectedTerm, selectedClass, selectedSubject]);

  useEffect(() => {
    if (!selectedTerm || !selectedClass) {
      setReportCards([]);
      setAnalysisAttendanceRecords([]);
      setAnalysisHistory([]);
      setAnalysisError(null);
      return;
    }

    let cancelled = false;
    const requestId = analysisRequestRef.current + 1;
    analysisRequestRef.current = requestId;

    const schoolId = getStoredSchoolId();
    const currentTerm = terms.find((term) => String(term._id) === String(selectedTerm));
    const comparableTerms = getComparableTerms(terms, selectedTerm);
    const currentTermIds = new Set([String(selectedTerm)]);

    const getTermAnalysisData = async (term) => {
      const termId = String(term?._id || term || '');
      const label = getTermLabel(term);
      const cacheKey = `${schoolId}|${selectedClass}|${termId}`;

      if (analysisCacheRef.current.has(cacheKey)) {
        return analysisCacheRef.current.get(cacheKey);
      }

      const [reportsRes, attendanceRes] = await Promise.all([
        api.get('/sba-v2/reports', {
          params: {
            schoolId,
            classId: selectedClass,
            termId,
          },
        }),
        api.get('/attendance/class/term', {
          params: {
            classId: selectedClass,
            termId,
          },
        }).catch((attendanceError) => {
          console.warn('Attendance analysis not available:', attendanceError);
          return { data: [] };
        }),
      ]);

      const attendanceRecords = extractAttendanceRecords(attendanceRes.data);
      const value = {
        termId,
        label,
        reports: extractReportCards(reportsRes.data),
        attendanceRecords,
        attendanceSummary: calculateAttendanceSummary(attendanceRecords),
      };
      analysisCacheRef.current.set(cacheKey, value);
      return value;
    };

    const fetchClassAnalysis = async () => {
      try {
        setLoadingAnalysis(true);
        setAnalysisError(null);
        setReportCards([]);
        setAnalysisAttendanceRecords([]);
        setAnalysisHistory([]);

        const currentData = await getTermAnalysisData(currentTerm || { _id: selectedTerm });
        if (cancelled || requestId !== analysisRequestRef.current) return;

        setReportCards(currentData.reports);
        setAnalysisAttendanceRecords(currentData.attendanceRecords);
        setAnalysisHistory([currentData]);
        setLoadingAnalysis(false);

        const previousHistory = await Promise.all(
          comparableTerms
            .filter((term) => !currentTermIds.has(String(term._id)))
            .map(async (term) => {
              try {
                return await getTermAnalysisData(term);
              } catch (termError) {
                console.warn('Previous term analysis not available:', termError);
                return {
                  termId: String(term._id),
                  label: getTermLabel(term),
                  reports: [],
                  attendanceRecords: [],
                  attendanceSummary: calculateAttendanceSummary([]),
                };
              }
            })
        );

        if (cancelled || requestId !== analysisRequestRef.current) return;
        setAnalysisHistory([...previousHistory, currentData]);
      } catch (err) {
        if (cancelled || requestId !== analysisRequestRef.current) return;
        console.error('Failed to fetch class performance analysis', err);
        setReportCards([]);
        setAnalysisAttendanceRecords([]);
        setAnalysisHistory([]);
        setAnalysisError(err.response?.data?.message || 'Failed to load class performance analysis.');
        setLoadingAnalysis(false);
      } finally {
        if (!cancelled && requestId === analysisRequestRef.current) {
          setLoadingAnalysis(false);
        }
      }
    };

    fetchClassAnalysis();

    return () => {
      cancelled = true;
    };
  }, [selectedTerm, selectedClass, terms]);

  const selectedTermObj = useMemo(
    () => terms.find((term) => String(term._id) === String(selectedTerm)),
    [terms, selectedTerm]
  );
  const selectedClassObj = useMemo(
    () => classes.find((classItem) => String(classItem._id) === String(selectedClass)),
    [classes, selectedClass]
  );
  const selectedSubjectObj = useMemo(
    () => subjects.find((subject) => String(subject._id) === String(selectedSubject)),
    [subjects, selectedSubject]
  );
  const selectedTermName = selectedTermObj?.name || selectedTermObj?.term || '';
  const selectedTermDisplayName = selectedTermObj ? getTermLabel(selectedTermObj) : '';
  const selectedClassName = selectedClassObj?.displayName || selectedClassObj?.name || selectedClassObj?.className || '';
  const selectedSubjectName = selectedSubjectObj?.name || selectedSubjectObj?.subjectName || '';
  const classAnalysis = useMemo(
    () => buildClassAnalysis(reportCards, analysisAttendanceRecords, analysisHistory),
    [reportCards, analysisAttendanceRecords, analysisHistory]
  );

  const handlePrint = () => {
    window.print();
  };

  const handleGenerateReportCards = async () => {
    if (!selectedClass || !selectedTerm) {
      alert("Please select at least a Term and a Class to generate report cards.");
      return;
    }

    try {
      setGeneratingPdf(true);
      
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;
      const schoolId = user?.school?._id || user?.school || '';

      const res = await api.get(`/sba-v2/reports`, {
        params: {
          schoolId,
          classId: selectedClass,
          termId: selectedTerm
        }
      });

      if (res.data && res.data.success && res.data.data.length > 0) {
        // The V2 controller resolves the roster for this term, including promoted students.
        const reportCardsData = res.data.data;
        
        const className = classes.find(c => c._id === selectedClass)?.displayName || classes.find(c => c._id === selectedClass)?.name || 'N/A';
        const termName = terms.find(t => t._id === selectedTerm)?.name || terms.find(t => t._id === selectedTerm)?.term || 'N/A';
        const academicYear = terms.find(t => t._id === selectedTerm)?.academicYear || new Date().getFullYear();
        const backendURL = api.defaults.baseURL.replace(/\/api$/, '');
        const logo = schoolInfoDetails?.school?.logo || schoolInfoDetails?.logo || user?.school?.logo;
        const adminSignature = schoolInfoDetails?.headTeacherSignature || schoolInfoDetails?.headteacherSignature;
        const makeAbsoluteUrl = (value) => {
          if (!value) return null;
          if (value.startsWith('http') || value.startsWith('data:')) return value;
          return `${backendURL}${value.startsWith('/') ? '' : '/'}${value}`;
        };
        
        const unifiedSchoolInfo = {
          name: schoolInfoDetails?.school?.name || user?.school?.name || 'School Name',
          address: schoolInfoDetails?.address || user?.school?.address || 'School Address',
          email: schoolInfoDetails?.email || user?.school?.email || 'email@school.com',
          phone: schoolInfoDetails?.phone || user?.school?.phone || '+123456789',
          motto: schoolInfoDetails?.motto || user?.school?.motto || '',
          logo: makeAbsoluteUrl(logo),
          adminName: schoolInfoDetails?.headTeacherName || user?.name || user?.user?.name || 'Admin',
          adminSignature: makeAbsoluteUrl(adminSignature),
        };

        // Fetch Attendance Data
        let attendanceData = [];
        let timesOpen = 0;
        try {
          const termDataObj = terms.find(t => t._id === selectedTerm);
          const attRes = await api.get('/attendance/class/term', {
            params: {
              classId: selectedClass,
              termId: selectedTerm,
            },
          });
          attendanceData = attRes.data || [];
          
          const isDayOpen = (wNum, apiDay) => {
            return attendanceData.some((stu) => {
              const weekRecord = stu.attendance?.find((a) => a.weekNumber === wNum);
              if (!weekRecord || !weekRecord.days) return false;
              const val = weekRecord.days[apiDay];
              return val && val !== 'notmarked';
            });
          };

          let maxWeekInAttendance = 0;
          attendanceData.forEach(stu => {
            stu.attendance?.forEach(wk => {
              if (wk.weekNumber > maxWeekInAttendance) maxWeekInAttendance = wk.weekNumber;
            });
          });

          let currentWeekToRender = maxWeekInAttendance > 0 ? maxWeekInAttendance : (typeof termDataObj?.weeks === 'number' ? termDataObj.weeks : 12);
          
          let cumulativeOpen = 0;
          for (let i = 1; i <= currentWeekToRender; i++) {
            let weekOpenCount = 0;
            ['M', 'T', 'W', 'TH', 'F'].forEach(apiDay => {
              if (isDayOpen(i, apiDay)) weekOpenCount++;
            });
            cumulativeOpen += weekOpenCount;
          }
          timesOpen = cumulativeOpen;
        } catch (err) {
          console.error('Error fetching attendance for report cards:', err);
        }

        // Ensure student profile pictures are absolute URLs and attach attendance
        const processedReportCards = reportCardsData.map(report => {
          if (report.studentInfo) {
            if (report.studentInfo.profilePicture && !report.studentInfo.profilePicture.startsWith('http')) {
              report.studentInfo.profilePicture = `${backendURL}${report.studentInfo.profilePicture.startsWith('/') ? '' : '/'}${report.studentInfo.profilePicture}`;
            }
            
            const stuAtt = attendanceData.find(a =>
              String(a.studentDocId || a._id || '') === String(report.studentInfo.studentDocId || report.studentInfo.id) ||
              String(a.user?._id || a.user || '') === String(report.studentInfo.studentUserId || report.studentInfo.id)
            );

            if (stuAtt) {
              let totalPresent = 0;
              stuAtt.attendance?.forEach(wk => {
                totalPresent += (wk.totalPresent || 0);
              });
              report.studentInfo.attendance = totalPresent;
            }
            report.studentInfo.timesOpen = timesOpen;
          }
          return report;
        });

        // Get term details for Next Term Begins
        const selectedTermObj = terms.find(t => t._id === selectedTerm);
        const teacherNextTermBegins = processedReportCards.find(report => report.studentInfo?.nextTermBegins)?.studentInfo?.nextTermBegins;
        const nextTermBegins = teacherNextTermBegins || selectedTermObj?.nextTermBegins || selectedTermObj?.vacationDate || 'TBD';
        const vacationDate = selectedTermObj?.endDate ? new Date(selectedTermObj.endDate) : null;
        const reportDate = vacationDate && !Number.isNaN(vacationDate.getTime())
          ? vacationDate.toLocaleDateString('en-GB', { timeZone: 'UTC' })
          : 'TBD';

        const [{ pdf }, { ClassReportCardsDocument }] = await Promise.all([
          import('@react-pdf/renderer'),
          import('../components/pdf/StudentReportCardPDF'),
        ]);

        // Generate the PDF document
        const doc = <ClassReportCardsDocument 
          reportCards={processedReportCards} 
          schoolInfo={unifiedSchoolInfo} 
          className={className} 
          termName={termName} 
          academicYear={academicYear} 
          noOnRoll={reportCardsData.length}
          nextTermBegins={nextTermBegins}
          reportDate={reportDate}
        />;
        
        const blob = await pdf(doc).toBlob();
        const url = URL.createObjectURL(blob);
        
        // Trigger download
        const link = document.createElement('a');
        link.href = url;
        link.download = `ReportCards_${className}_${termName}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        alert("No records found for the selected class and term.");
      }
    } catch (err) {
      console.error('Failed to generate report cards', err);
      alert("Failed to generate report cards. " + (err.response?.data?.message || err.message));
    } finally {
      setGeneratingPdf(false);
    }
  };

  if (loadingInitial) {
    return (
      <Container maxWidth="xl" sx={{ py: isMobile ? 2 : 4, px: isMobile ? 1 : 2 }}>
        <LoadingState />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: isMobile ? 2 : 4, px: isMobile ? 1 : 2 }} className="print-container">
      {/* Print-specific styles using a standard HTML style block to ensure it works */}
      <style>
        {`
          @media print {
            body {
              -webkit-print-color-adjust: exact;
            }
            .no-print {
              display: none !important;
            }
            .print-only {
              display: block !important;
            }
            /* Adjust table scaling for printing */
            table {
              page-break-inside: auto;
            }
            tr {
              page-break-inside: avoid;
              page-break-after: auto;
            }
            th, td {
              font-size: 10pt !important;
              padding: 4px !important;
            }
            /* Hide the sidebar and top navbar usually defined by layout classes */
            header, nav, aside {
              display: none !important;
            }
            main {
              margin: 0 !important;
              padding: 0 !important;
              width: 100% !important;
            }
            .MuiDrawer-root {
               display: none !important;
            }
            .MuiAppBar-root {
               display: none !important;
            }
          }
        `}
      </style>

      {/* Header Section */}
      <PageHeader className="no-print" title={<>Exams and Report</>} description={<>View marks, analyze class performance, and generate report cards</>} actions={<>
        <Button variant="outlined" startIcon={generatingPdf ? <CircularProgress size={20} /> : <PdfIcon />} onClick={handleGenerateReportCards} disabled={generatingPdf || !selectedClass || !selectedTerm} fullWidth={isMobile}>
                  {generatingPdf ? "Generating..." : "Generate Report Cards"}
                </Button>
        <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint} disabled={records.length === 0} fullWidth={isMobile}>
                  Print Records
                </Button>
      </>}></PageHeader>
      <SummaryStrip container spacing={isMobile ? 2 : 3} sx={{ mb: isMobile ? 3 : 4 }} className="no-print">
        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <PageStatCard
            icon={<FactCheckIcon fontSize={isMobile ? 'small' : 'medium'} />}
            label="Report Cards"
            value={classAnalysis.studentsWithMarks || 0}
            helper={
              selectedClassName
                ? `${classAnalysis.totalStudents || 0} students in ${selectedClassName}`
                : 'Select a class to load reports'
            }
            color={theme.palette.primary.main}
            isMobile={isMobile}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <PageStatCard
            icon={<TrendingUpIcon fontSize={isMobile ? 'small' : 'medium'} />}
            label="Class Average"
            value={classAnalysis.studentsWithMarks ? formatPercent(classAnalysis.classAverage) : '0%'}
            helper={selectedTermDisplayName || 'Select an academic term'}
            color={theme.palette.success.main}
            isMobile={isMobile}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <PageStatCard
            icon={<GroupsIcon fontSize={isMobile ? 'small' : 'medium'} />}
            label="Need Support"
            value={classAnalysis.supportStudentCount || 0}
            helper="Students below 65 average"
            color={theme.palette.warning.main}
            isMobile={isMobile}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <PageStatCard
            icon={<WarningIcon fontSize={isMobile ? 'small' : 'medium'} />}
            label="Urgent Cases"
            value={classAnalysis.urgentStudentCount || 0}
            helper="Students below 50 average"
            color={theme.palette.error.main}
            isMobile={isMobile}
          />
        </Grid>
      </SummaryStrip>

      

      {selectedTermObj?.endDate && new Date(selectedTermObj.endDate) < new Date() && (
          <Alert severity="info" sx={{ mb: 2 }}>For previous terms, select the class the students attended then. Reports use the students saved in that term's marks, including students who have since been promoted.</Alert>
        )}

      {/* Filters Card */}
      <FilterBar sx={{ mb: 4, boxShadow: isMobile ? 1 : 3, border: `1px solid ${theme.palette.divider}` }} className="no-print">
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: isMobile ? 2 : 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Stack direction={isMobile ? "column" : "row"} justifyContent="space-between" alignItems={isMobile ? "flex-start" : "center"} spacing={isMobile ? 1 : 0}>
              <Typography variant="h6" fontWeight="600" sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}>
                Report Filters
              </Typography>
              {(selectedClassName || selectedTermDisplayName || selectedSubjectName) && !isMobile && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {selectedClassName && <Chip label={selectedClassName} color="primary" variant="outlined" />}
                  {selectedTermDisplayName && <Chip label={selectedTermDisplayName} color="success" variant="outlined" />}
                  {selectedSubjectName && <Chip label={selectedSubjectName} color="info" variant="outlined" />}
                </Stack>
              )}
            </Stack>
          </Box>
          <Box sx={{ p: isMobile ? 2 : 3 }}>
          <Grid container spacing={isMobile ? 2 : 3}>
            <Grid size={{ xs: 12, md: 4 }}   >
              <TextField
                select
                fullWidth
                size={isMobile ? "small" : "medium"}
                label="Academic Year / Term"
                value={selectedTerm}
                onChange={(e) => setSelectedTerm(e.target.value)}
                sx={filterFieldSx}
              >
                {terms.map((t) => (
                  <MenuItem key={t._id} value={t._id}>
                    {[t.academicYear, t.name || t.term || 'Unknown Term'].filter(Boolean).join(' • ')}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}   >
              <TextField
                select
                fullWidth
                size={isMobile ? "small" : "medium"}
                label="Select Class"
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                sx={filterFieldSx}
              >
                {classes.map((c) => (
                  <MenuItem key={c._id} value={c._id}>
                    {c.displayName || c.name || c.className || 'Unknown Class'}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}   >
              <TextField
                select
                fullWidth
                size={isMobile ? "small" : "medium"}
                label="Select Subject"
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                sx={filterFieldSx}
              >
                {(() => {
                  const currentClassObj = classes.find(c => c._id === selectedClass);
                  const displaySubjects = currentClassObj?.subjects && currentClassObj.subjects.length > 0 
                    ? currentClassObj.subjects 
                    : subjects; // global fallback

                  return displaySubjects.map((s) => (
                    <MenuItem key={s._id} value={s._id}>
                      {s.name || s.subjectName || 'Unknown Subject'}
                    </MenuItem>
                  ));
                })()}
              </TextField>
            </Grid>
          </Grid>
          </Box>
        </CardContent>
      </FilterBar>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} className="no-print">
          {error}
        </Alert>
      )}

      <ClassPerformanceAnalysisPanel
        loading={loadingAnalysis}
        error={analysisError}
        selectedClassName={selectedClassName}
        selectedTermName={selectedTermDisplayName || selectedTermName}
        analysis={classAnalysis}
      />

      {/* Print Only Header (Visible only on paper) */}
      <Box sx={{ display: 'none', mb: 2 }} className="print-only">
         <Typography variant="h5" align="center" fontWeight="bold" gutterBottom>
            SBA Records
         </Typography>
         <Typography align="center" gutterBottom>
            Class: {selectedClassName || 'N/A'} | 
            Subject: {selectedSubjectName || 'N/A'} |
            Term: {selectedTermDisplayName || selectedTermName || 'N/A'}
         </Typography>
      </Box>

      {/* Table Section */}
      <Card sx={{ boxShadow: isMobile ? 1 : 3, border: `1px solid ${theme.palette.divider}` }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: isMobile ? 2 : 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Stack direction={isMobile ? "column" : "row"} justifyContent="space-between" alignItems={isMobile ? "flex-start" : "center"} spacing={isMobile ? 1 : 0}>
              <Typography variant="h6" fontWeight="600" sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}>
                Subject Marks ({records.length})
              </Typography>
              {records.length > 0 && !isMobile && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip label={selectedClassName || 'Class'} color="primary" variant="outlined" />
                  <Chip label={selectedSubjectName || 'Subject'} color="success" variant="outlined" />
                </Stack>
              )}
            </Stack>
          </Box>
        <DataTable component={Paper} elevation={0} sx={{ width: '100%', overflow: 'auto', maxHeight: isTablet ? '60vh' : 'none' }}>
          {loadingMarks ? (
            <LoadingState />
          ) : records.length > 0 ? (
            <Table size="small" sx={{ minWidth: isTablet ? 900 : 'auto' }}>
              <TableHead sx={{ bgcolor: theme.palette.background.default }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', py: 2 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', py: 2 }}>STUDENT NAME</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', py: 2 }}>GROUP W. (10)</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', py: 2 }}>CLASS T1 (20)</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', py: 2 }}>CLASS T2 (20)</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', py: 2 }}>PROJECT (10)</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', py: 2 }}>EXAMS (100)</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', py: 2 }}>TOTAL (%)</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', py: 2 }}>GRADE</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
{records.length === 0 && <TableRow><TableCell colSpan={100}><EmptyState title="No records found" description="Records will appear here when available. Check your selected filters." /></TableCell></TableRow>}

                {records.map((rec, index) => (
                  <TableRow
                    key={rec.student}
                    hover
                    sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) }, transition: 'background-color 0.5s' }}
                  >
                    <TableCell sx={{ py: 2 }}>{index + 1}</TableCell>
                    <TableCell sx={{ py: 2, fontWeight: 600 }}>{rec.studentName || 'Unknown'}</TableCell>
                    <TableCell align="center" sx={{ py: 2 }}>{rec.classWork ?? '-'}</TableCell>
                    <TableCell align="center" sx={{ py: 2 }}>{rec.classTest1 ?? '-'}</TableCell>
                    <TableCell align="center" sx={{ py: 2 }}>{rec.classTest2 ?? '-'}</TableCell>
                    <TableCell align="center" sx={{ py: 2 }}>{rec.projectWork ?? '-'}</TableCell>
                    <TableCell align="center" sx={{ py: 2 }}>{rec.exams ?? '-'}</TableCell>
                    <TableCell align="center" sx={{ py: 2, fontWeight: 'bold', color: 'primary.main' }}>
                      {rec.total ?? '-'}
                    </TableCell>
                    <TableCell align="center" sx={{ py: 2 }}>
                      <Typography variant="body2" sx={{ 
                          fontWeight: 'bold', 
                          color: (rec.grade === 'F' || (rec.grade || '').includes('Beginning') || (rec.grade || '').includes('Bronze')) ? 'error.main' : 'text.primary' 
                      }}>
                        {rec.grade ?? '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Box sx={{ textAlign: 'center', py: isMobile ? 4 : 6, px: 2 }}>
              <AssessmentIcon sx={{ fontSize: isMobile ? 48 : 64, color: 'text.secondary', opacity: 0.5, mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No marks found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedClass && selectedSubject && selectedTerm 
                  ? 'No records found for the selected criteria.' 
                  : 'Please select a term, class, and subject.'}
              </Typography>
            </Box>
          )}
        </DataTable>
        </CardContent>
      </Card>
    </Container>
  );
}
